import {
  MagnifyingGlassIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BuildingOfficeIcon,
  UserIcon,
  XMarkIcon,
  TagIcon,
  FolderIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import toast from 'react-hot-toast';

import { Button, Card, Avatar, Spinner } from '@realvisor/ui';
import {
  useContacts,
  useCompanies,
  useBulkTemperature,
  useBulkAddTags,
  useBulkRemoveTags,
  useBulkMoveProject,
  useBulkArchive,
} from '@/hooks';
import { useProject } from '@/contexts/ProjectContext';

const PAGE_SIZE = 50;
type Tab = 'contacts' | 'companies';

export default function Contacts() {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [tab, setTab] = useState<Tab>('contacts');
  const [debouncedSearch] = useDebounce(search, 300);
  const { currentProjectId, isAllProjects, currentProject, projects } = useProject();

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<string | null>(null);
  const [tagInput, setTagInput] = useState('');
  const [moveProjectId, setMoveProjectId] = useState('');

  // Bulk hooks
  const bulkTemperature = useBulkTemperature();
  const bulkAddTags = useBulkAddTags();
  const bulkRemoveTags = useBulkRemoveTags();
  const bulkMoveProject = useBulkMoveProject();
  const bulkArchive = useBulkArchive();

  useEffect(() => {
    setPage(1);
  }, [currentProjectId, debouncedSearch, tab]);

  // Clear selection on page/filter change
  useEffect(() => {
    setSelectedIds(new Set());
    setBulkAction(null);
  }, [page, currentProjectId, debouncedSearch, tab]);

  const { data: contactsData, isLoading: contactsLoading, error: contactsError } = useContacts({
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch || undefined,
    projectId: isAllProjects ? undefined : currentProjectId ?? undefined,
  });

  const { data: companiesData, isLoading: companiesLoading, error: companiesError } = useCompanies({
    page,
    limit: PAGE_SIZE,
    search: debouncedSearch || undefined,
    projectId: isAllProjects ? undefined : currentProjectId ?? undefined,
  });

  const contacts = contactsData?.data || [];
  const contactsMeta = contactsData?.meta;
  const companies = companiesData?.data || [];
  const companiesMeta = companiesData?.meta;

  const isLoading = tab === 'contacts' ? contactsLoading : companiesLoading;
  const error = tab === 'contacts' ? contactsError : companiesError;
  const meta = tab === 'contacts' ? contactsMeta : companiesMeta;

  const isAnyBulkPending =
    bulkTemperature.isPending ||
    bulkAddTags.isPending ||
    bulkRemoveTags.isPending ||
    bulkMoveProject.isPending ||
    bulkArchive.isPending;

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (tab !== 'contacts') return;
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map((c) => c.id)));
    }
  }, [contacts, selectedIds.size, tab]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setBulkAction(null);
  }, []);

  const contactIdsArray = Array.from(selectedIds);

  const handleBulkTemperature = async (temp: string) => {
    try {
      await bulkTemperature.mutateAsync({ contactIds: contactIdsArray, temperature: temp });
      toast.success(`Teplota změněna u ${contactIdsArray.length} kontaktů`);
      clearSelection();
    } catch {
      toast.error('Chyba při změně teploty');
    }
  };

  const handleBulkAddTags = async () => {
    const tags = tagInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    if (tags.length === 0) {
      toast.error('Zadejte alespoň jeden tag');
      return;
    }
    try {
      await bulkAddTags.mutateAsync({ contactIds: contactIdsArray, tags });
      toast.success(`Přidáno ${tags.length} tagů u ${contactIdsArray.length} kontaktů`);
      setTagInput('');
      clearSelection();
    } catch {
      toast.error('Chyba při přidávání tagů');
    }
  };

  const handleBulkRemoveTags = async () => {
    const tags = tagInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    if (tags.length === 0) {
      toast.error('Zadejte alespoň jeden tag');
      return;
    }
    try {
      await bulkRemoveTags.mutateAsync({ contactIds: contactIdsArray, tags });
      toast.success(`Odebráno ${tags.length} tagů u ${contactIdsArray.length} kontaktů`);
      setTagInput('');
      clearSelection();
    } catch {
      toast.error('Chyba při odebírání tagů');
    }
  };

  const handleBulkMoveProject = async () => {
    if (!moveProjectId) {
      toast.error('Vyberte projekt');
      return;
    }
    try {
      await bulkMoveProject.mutateAsync({ contactIds: contactIdsArray, projectId: moveProjectId });
      toast.success(`${contactIdsArray.length} kontaktů přesunuto`);
      setMoveProjectId('');
      clearSelection();
    } catch {
      toast.error('Chyba při přesunu');
    }
  };

  const handleBulkArchive = async () => {
    if (!confirm(`Opravdu archivovat ${contactIdsArray.length} kontaktů?`)) return;
    try {
      await bulkArchive.mutateAsync({ contactIds: contactIdsArray });
      toast.success(`${contactIdsArray.length} kontaktů archivováno`);
      clearSelection();
    } catch {
      toast.error('Chyba při archivaci');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kontakty</h1>
          <p className="text-sm text-gray-500">
            {contactsMeta && companiesMeta
              ? `${contactsMeta.total.toLocaleString('cs-CZ')} kontaktů, ${companiesMeta.total.toLocaleString('cs-CZ')} firem${currentProject ? ` v projektu ${currentProject.name}` : ''}`
              : 'Správa kontaktů a firem'}
          </p>
        </div>
        <Button>
          <PlusIcon className="mr-2 h-4 w-4" />
          {tab === 'contacts' ? 'Nový kontakt' : 'Nová firma'}
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setTab('contacts')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'contacts'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <UserIcon className="h-4 w-4" />
          Kontakty
          {contactsMeta && (
            <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              {contactsMeta.total.toLocaleString('cs-CZ')}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('companies')}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'companies'
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <BuildingOfficeIcon className="h-4 w-4" />
          Firmy
          {companiesMeta && (
            <span className="ml-1 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              {companiesMeta.total.toLocaleString('cs-CZ')}
            </span>
          )}
        </button>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && tab === 'contacts' && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-blue-800">
              Vybráno {selectedIds.size} kontaktů
            </span>
            <div className="h-4 w-px bg-blue-300" />

            {/* Temperature buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => handleBulkTemperature('hot')}
                disabled={isAnyBulkPending}
                className="inline-flex items-center gap-1 rounded-md bg-red-100 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-200 disabled:opacity-50 transition-colors"
              >
                Horky
              </button>
              <button
                onClick={() => handleBulkTemperature('warm')}
                disabled={isAnyBulkPending}
                className="inline-flex items-center gap-1 rounded-md bg-orange-100 px-2.5 py-1.5 text-xs font-medium text-orange-700 hover:bg-orange-200 disabled:opacity-50 transition-colors"
              >
                Teply
              </button>
              <button
                onClick={() => handleBulkTemperature('cold')}
                disabled={isAnyBulkPending}
                className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2.5 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-200 disabled:opacity-50 transition-colors"
              >
                Studeny
              </button>
            </div>

            <div className="h-4 w-px bg-blue-300" />

            {/* Tag action */}
            <button
              onClick={() => setBulkAction(bulkAction === 'tags' ? null : 'tags')}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                bulkAction === 'tags'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <TagIcon className="h-3.5 w-3.5" />
              Tagy
            </button>

            {/* Move project */}
            <button
              onClick={() => setBulkAction(bulkAction === 'move' ? null : 'move')}
              className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                bulkAction === 'move'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <FolderIcon className="h-3.5 w-3.5" />
              Přesunout
            </button>

            {/* Archive */}
            <button
              onClick={handleBulkArchive}
              disabled={isAnyBulkPending}
              className="inline-flex items-center gap-1 rounded-md bg-white text-red-600 border border-red-200 px-2.5 py-1.5 text-xs font-medium hover:bg-red-50 disabled:opacity-50 transition-colors"
            >
              <ArchiveBoxIcon className="h-3.5 w-3.5" />
              Archivovat
            </button>

            <div className="flex-1" />

            {/* Clear selection */}
            <button
              onClick={clearSelection}
              className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="h-4 w-4" />
              Zrušit výběr
            </button>
          </div>

          {/* Expanded sub-action: Tags */}
          {bulkAction === 'tags' && (
            <div className="mt-3 flex items-center gap-2 border-t border-blue-200 pt-3">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="tag1, tag2, ..."
                className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleBulkAddTags();
                }}
              />
              <Button
                size="sm"
                onClick={handleBulkAddTags}
                disabled={isAnyBulkPending || !tagInput.trim()}
              >
                Přidat
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkRemoveTags}
                disabled={isAnyBulkPending || !tagInput.trim()}
              >
                Odebrat
              </Button>
            </div>
          )}

          {/* Expanded sub-action: Move project */}
          {bulkAction === 'move' && (
            <div className="mt-3 flex items-center gap-2 border-t border-blue-200 pt-3">
              <select
                value={moveProjectId}
                onChange={(e) => setMoveProjectId(e.target.value)}
                className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm"
              >
                <option value="">Vyberte cílový projekt</option>
                {projects?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                onClick={handleBulkMoveProject}
                disabled={isAnyBulkPending || !moveProjectId}
              >
                Přesunout
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Search */}
      <Card>
        <div className="p-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder={tab === 'contacts' ? 'Hledat kontakty...' : 'Hledat firmy...'}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-10 pr-4 text-sm placeholder-gray-500 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </Card>

      {/* List */}
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="px-6 py-12 text-center">
            <p className="text-red-500">Chyba při načítání</p>
          </div>
        ) : tab === 'contacts' && contacts.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500">
              {debouncedSearch ? 'Žádné kontakty neodpovídají hledání' : 'Zatím nemáte žádné kontakty'}
            </p>
          </div>
        ) : tab === 'companies' && companies.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500">
              {debouncedSearch ? 'Žádné firmy neodpovídají hledání' : 'Zatím nemáte žádné firmy'}
            </p>
          </div>
        ) : (
          <>
            {/* Select all header for contacts */}
            {tab === 'contacts' && contacts.length > 0 && (
              <div className="flex items-center gap-3 border-b border-gray-200 px-4 py-2 bg-gray-50">
                <input
                  type="checkbox"
                  checked={selectedIds.size === contacts.length && contacts.length > 0}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-500">
                  {selectedIds.size > 0
                    ? `Vybráno ${selectedIds.size} z ${contacts.length}`
                    : 'Vybrat vše'}
                </span>
              </div>
            )}

            <div className="divide-y divide-gray-200">
              {tab === 'contacts'
                ? contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className={`flex items-center gap-4 p-4 transition-colors ${
                        selectedIds.has(contact.id) ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedIds.has(contact.id)}
                        onChange={() => toggleSelect(contact.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 shrink-0"
                      />
                      <Link
                        to={`/contacts/${contact.id}`}
                        className="flex items-center gap-4 flex-1 min-w-0"
                      >
                        <Avatar name={`${contact.firstName} ${contact.lastName || ''}`} size="md" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-gray-900">
                              {contact.firstName} {contact.lastName}
                            </p>
                            {(contact as any).temperature && (contact as any).temperature !== 'cold' && (
                              <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                                  (contact as any).temperature === 'hot'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-orange-100 text-orange-700'
                                }`}
                              >
                                {(contact as any).temperature === 'hot' ? 'Horky' : 'Teply'}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 truncate">
                            {contact.email || 'Bez emailu'}
                            {(contact as any).company?.name && (
                              <span className="ml-2 text-xs text-blue-600">
                                • {(contact as any).company.name}
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">{contact.phone || '-'}</p>
                          <p className="text-xs text-gray-400">{contact._count?.leads || 0} leadů</p>
                        </div>
                      </Link>
                    </div>
                  ))
                : companies.map((company) => (
                    <Link
                      key={company.id}
                      to={`/companies/${company.id}`}
                      className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 shrink-0">
                        <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900">{company.name}</p>
                        <p className="text-sm text-gray-500 truncate">
                          {company.ico ? `IČO: ${company.ico}` : ''}
                          {company.ico && company.city ? ' · ' : ''}
                          {company.city || ''}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-500">{company.phone || company.email || '-'}</p>
                        <p className="text-xs text-gray-400">{company._count?.contacts || 0} kontaktů</p>
                      </div>
                    </Link>
                  ))}
            </div>

            {/* Pagination */}
            {meta && meta.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
                <p className="text-sm text-gray-500">
                  {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, meta.total)} z{' '}
                  {meta.total.toLocaleString('cs-CZ')}
                </p>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(1)}>
                    «
                  </Button>
                  <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    <ChevronLeftIcon className="h-4 w-4" />
                  </Button>
                  {Array.from({ length: Math.min(5, meta.totalPages) }, (_, i) => {
                    let p: number;
                    if (meta.totalPages <= 5) p = i + 1;
                    else if (page <= 3) p = i + 1;
                    else if (page >= meta.totalPages - 2) p = meta.totalPages - 4 + i;
                    else p = page - 2 + i;
                    return (
                      <Button key={p} variant={p === page ? 'primary' : 'outline'} size="sm" onClick={() => setPage(p)}>
                        {p}
                      </Button>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= meta.totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= meta.totalPages}
                    onClick={() => setPage(meta.totalPages)}
                  >
                    »
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
